import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";

/** Fade/slide children in as they enter the viewport. */
export function Reveal({ children, delay = 0, y = 28, className = "", as = "div", once = true }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  return (
    <Tag className={className}
         initial={reduce ? false : { opacity: 0, y }}
         whileInView={{ opacity: 1, y: 0 }}
         viewport={{ once, margin: "-80px" }}
         transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </Tag>
  );
}

/** Moves its children at a different speed than the page scroll (the parallax effect). */
export function ParallaxLayer({ children, speed = 0.2, className = "", style = {}, target }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: target || ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`${-speed * 100}px`, `${speed * 100}px`]);
  return <motion.div ref={ref} className={className} style={reduce ? style : { ...style, y }}>{children}</motion.div>;
}

/** Parallax relative to the first screen of a section (hero): layers drift as you scroll away. */
export function useHeroParallax(ref) {
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  return scrollYProgress;
}

export function CountUp({ value, duration = 1.4, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const [n, setN] = useState(reduce ? value : 0);
  useEffect(() => {
    if (!inView || reduce) { if (reduce) setN(value); return undefined; }
    let raf, start;
    const step = (t) => {
      start = start ?? t;
      const p = Math.min((t - start) / (duration * 1000), 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, reduce]);
  return <span ref={ref} className={className}>{n.toLocaleString("en-IN")}</span>;
}
